import { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { KeyIcon, ClipboardDocumentIcon, CheckIcon, ChevronDownIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import { Button } from '@follow/ui';
import { Lock } from '../../services/models/assets';
import CustomerSelect, { CustomerValue, emptyCustomerValue } from '../CustomerSelect';

interface ModalGenerateCodeProps {
  visible: boolean;
  lock: Lock | null;
  onCancel: () => void;
  onSubmit: (data: {
    lockId: number;
    startDate: string | null;
    duration: number;
    durationUnit: 'hours' | 'days';
    price?: number;
    deposit?: number;
    userId?: number;
    customer?: { firstName?: string; lastName?: string; phone?: string; email?: string };
    accessOnly: boolean;
  }) => void;
  isLoading?: boolean;
  error?: string | null;
  result?: { token: string } | null;
}

const ModalGenerateCode: React.FC<ModalGenerateCodeProps> = ({
  visible,
  lock,
  onCancel,
  onSubmit,
  isLoading,
  error,
  result,
}) => {
  const [immediate, setImmediate] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [duration, setDuration] = useState<number | ''>(24);
  const [durationUnit, setDurationUnit] = useState<'hours' | 'days'>('hours');
  const [copied, setCopied] = useState(false);
  // A manual rental (default) reserves the lock and can be charged in cash later.
  // "Accès seul" falls back to a standalone code with no rental/price.
  const [accessOnly, setAccessOnly] = useState(false);
  const [price, setPrice] = useState('');
  const [deposit, setDeposit] = useState('');
  const [customer, setCustomer] = useState<CustomerValue>(emptyCustomerValue);
  const dateInputRef = useRef<HTMLInputElement>(null);

  const toDatetimeLocalValue = (date: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const formatDateFR = (isoValue: string) => {
    if (!isoValue) return '';
    const d = new Date(isoValue);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  useEffect(() => {
    if (visible) {
      setImmediate(false);
      setStartDate('');
      setDuration(24);
      setDurationUnit('hours');
      setCopied(false);
      setAccessOnly(false);
      setPrice('');
      setDeposit('');
      setCustomer(emptyCustomerValue);
    }
  }, [visible]);

  const handleSubmit = () => {
    if (!lock) return;

    const hasUser = customer.userId != null;
    const hasFreeText = !!(customer.firstName || customer.lastName || customer.phone || customer.email);
    const customerPayload = (!accessOnly && !hasUser && hasFreeText)
      ? {
          firstName: customer.firstName || undefined,
          lastName: customer.lastName || undefined,
          phone: customer.phone || undefined,
          email: customer.email || undefined,
        }
      : undefined;

    onSubmit({
      lockId: lock.id,
      // datetime-local values carry no timezone: convert to UTC ISO so the
      // server doesn't interpret the wall-clock time in its own timezone
      startDate: immediate || !startDate ? null : new Date(startDate).toISOString(),
      duration: duration || 1,
      durationUnit,
      accessOnly,
      price: !accessOnly && price !== '' ? Number(price) : undefined,
      deposit: !accessOnly && deposit !== '' ? Number(deposit) : undefined,
      userId: !accessOnly && hasUser ? customer.userId : undefined,
      customer: customerPayload,
    });
  };

  const handleCopy = async () => {
    if (!result?.token) return;
    await navigator.clipboard.writeText(result.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isValid = (immediate || startDate) && (duration !== '' && duration > 0);

  const handleClose = () => {
    setImmediate(true);
    setStartDate('');
    setDuration(24);
    setDurationUnit('hours');
    setCopied(false);
    onCancel();
  };

  return (
    <Transition.Root show={visible} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-neutral-30 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 transition-all">
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-100">
                    <KeyIcon className="h-5 w-5 text-primary-600" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Dialog.Title as="h3" className="text-neutral typo-label">
                      {result
                        ? (accessOnly ? 'Accès généré' : 'Location créée')
                        : (accessOnly ? 'Générer un accès seul' : 'Créer une location (espèces)')}
                    </Dialog.Title>
                    <p className="text-neutral-80 typo-small">
                      Lock {lock?.position != null ? lock.position + 1 : ''}
                    </p>
                  </div>
                </div>

                {result ? (
                  <>
                    {/* Success state */}
                    <div className="flex flex-col items-center gap-4 mb-6">
                      <div className="flex items-center gap-2 p-4 bg-neutral-5 rounded-lg w-full justify-center">
                        <span className="font-mono text-2xl font-bold text-neutral tracking-wider select-all">
                          {result.token}
                        </span>
                        <button
                          type="button"
                          onClick={handleCopy}
                          className="p-1.5 rounded-md hover:bg-neutral-10 transition-colors"
                          title="Copier"
                        >
                          {copied ? (
                            <CheckIcon className="h-5 w-5 text-success-500" />
                          ) : (
                            <ClipboardDocumentIcon className="h-5 w-5 text-neutral-40" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Button variant="primary" size="sm" onClick={handleClose}>
                        Fermer
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Form state */}
                    {/* Mode: manual rental (default) vs standalone access code */}
                    <label className="flex items-center gap-2 mb-4 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={accessOnly}
                        onChange={(e) => setAccessOnly(e.target.checked)}
                        className="rounded"
                      />
                      <span className="text-neutral-60 typo-small">Accès seul (sans location ni paiement)</span>
                    </label>

                    {/* Start date */}
                    <div className="flex flex-col gap-2 mb-4">
                      <label className="text-neutral-60 typo-small">Date d'activation</label>
                      <div className="w-full flex flex-col gap-2">
                        <div className={`relative ${immediate ? 'opacity-40' : ''}`}>
                          <button
                            type="button"
                            onClick={() => {
                              if (immediate) setImmediate(false);
                              dateInputRef.current?.showPicker();
                            }}
                            className="w-full px-3 py-2 rounded-md outline outline-1 outline-offset-[-1px] outline-neutral-20 bg-neutral-5 typo-small text-left"
                          >
                            {formatDateFR(startDate) || <span className="text-neutral-40">jj/mm/aaaa hh:mm</span>}
                          </button>
                          <CalendarDaysIcon className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-40" />
                          <input
                            ref={dateInputRef}
                            type="datetime-local"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="absolute inset-0 opacity-0 pointer-events-none"
                            tabIndex={-1}
                          />
                        </div>
                        <label className="flex items-center gap-1.5 cursor-pointer mr-auto">
                          <input
                            type="checkbox"
                            checked={immediate}
                            onChange={(e) => {
                              setImmediate(e.target.checked);
                              if (e.target.checked) setStartDate(toDatetimeLocalValue(new Date()));
                            }}
                            className="rounded"
                          />
                          <span className="text-neutral-60 typo-small">Immédiat</span>
                        </label>
                      </div>
                    </div>

                    {/* Duration */}
                    <div className="flex flex-col gap-2 mb-6">
                      <label className="text-neutral-60 typo-small">Durée de validité</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={duration}
                          onChange={(e) => {
                            const val = e.target.value;
                            setDuration(val === '' ? '' : Math.max(1, Math.floor(Number(val))));
                          }}
                          onBlur={() => { if (duration === '' || duration < 1) setDuration(1); }}
                          min={1}
                          className="w-20 px-3 py-2 rounded-md outline outline-1 outline-offset-[-1px] outline-neutral-20 bg-neutral-5 typo-small"
                        />
                        <div className="relative">
                          <select
                            value={durationUnit}
                            onChange={(e) => {
                              const newUnit = e.target.value as 'hours' | 'days';
                              if (newUnit === 'days' && durationUnit === 'hours' && duration === 24) {
                                setDuration(7);
                              } else if (newUnit === 'hours' && durationUnit === 'days' && duration === 7) {
                                setDuration(24);
                              }
                              setDurationUnit(newUnit);
                            }}
                            className="appearance-none pl-3 pr-8 py-1.5 rounded-md border border-neutral-10 bg-white text-neutral hover:text-neutral hover:bg-neutral-5 transition-colors typo-body cursor-pointer"
                          >
                            <option value="hours">Heures</option>
                            <option value="days">Jours</option>
                          </select>
                          <ChevronDownIcon className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-40" strokeWidth={3} />
                        </div>
                      </div>
                      {(() => {
                        const dur = duration !== '' && duration > 0 ? duration : null;
                        const start = dur
                          ? (immediate ? new Date() : (startDate ? new Date(startDate) : null))
                          : null;
                        const isDateVisible = start && !isNaN(start.getTime());
                        const end = isDateVisible ? new Date(start) : new Date();
                        if (isDateVisible && dur) {
                          if (durationUnit === 'hours') {
                            end.setHours(end.getHours() + dur);
                          } else {
                            end.setDate(end.getDate() + dur);
                          }
                        }
                        return (
                          <p className={`typo-small text-neutral-40 ${isDateVisible ? '' : 'opacity-0'}`}>
                            Fin de l'accès : {end.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}{' '}
                            à {end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        );
                      })()}
                    </div>

                    {!accessOnly && (
                      <div className="flex flex-col gap-3 mb-6 pt-4 border-t border-neutral-10">
                        <div className="flex items-center gap-3">
                          <div className="flex flex-col gap-1">
                            <label className="text-neutral-60 typo-small">Prix (€)</label>
                            <input
                              type="number"
                              min={0}
                              step="0.01"
                              value={price}
                              onChange={(e) => setPrice(e.target.value)}
                              placeholder="0"
                              className="w-24 px-3 py-2 rounded-md outline outline-1 outline-offset-[-1px] outline-neutral-20 bg-neutral-5 typo-small"
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className="text-neutral-60 typo-small">Caution (€)</label>
                            <input
                              type="number"
                              min={0}
                              step="0.01"
                              value={deposit}
                              onChange={(e) => setDeposit(e.target.value)}
                              placeholder="0"
                              className="w-24 px-3 py-2 rounded-md outline outline-1 outline-offset-[-1px] outline-neutral-20 bg-neutral-5 typo-small"
                            />
                          </div>
                        </div>
                        <label className="text-neutral-60 typo-small">Client (optionnel)</label>
                        <CustomerSelect value={customer} onChange={setCustomer} />
                      </div>
                    )}

                    {error && (
                      <div className="mb-4 p-3 bg-error-100 rounded-md">
                        <span className="text-error-600 typo-small">{error}</span>
                      </div>
                    )}

                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={handleClose}>
                        Annuler
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        tone='light'
                        onClick={handleSubmit}
                        disabled={!isValid || isLoading}
                      >
                        {isLoading ? 'Génération...' : (accessOnly ? 'Générer l\'accès' : 'Créer la location')}
                      </Button>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ModalGenerateCode;
